import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { PredefinedObstacle } from "../../../../hooks/usePredefinedObstacle";
import { DimensionInput } from "./DimensionInput";
import { EditFormData } from "../index";

interface Props {
  open: boolean;
  obstacle: PredefinedObstacle | null;
  isCreating: boolean;
  onClose: () => void;
  onSave: (data: EditFormData) => Promise<void>;
}

export const ObstacleEditDialog: React.FC<Props> = ({
  open,
  obstacle,
  isCreating,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = React.useState<EditFormData>({
    name: "",
    width: "",
    length: "",
    height: "",
    image: undefined,
  });
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset form when obstacle changes or creating new
  React.useEffect(() => {
    if (isCreating) {
      setFormData({
        name: "",
        width: "",
        length: "",
        height: "",
        image: undefined,
      });
      setImagePreview(null);
      setImageError(null);
    } else if (obstacle) {
      setFormData({
        name: obstacle.name,
        width: "",
        length: "",
        height: "",
        image: undefined,
      });
      setImagePreview(null);
      setImageError(null);
    }
  }, [obstacle, isCreating]);

  const handleChange = (field: keyof EditFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Clear previous error
      setImageError(null);
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setImageError("File size must be under 10MB");
        event.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file type (images only)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setImageError("Only image files are allowed (JPG, PNG, GIF, SVG)");
        event.target.value = ''; // Clear the input
        return;
      }
      
      setFormData(prev => ({ ...prev, image: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    await onSave(formData);
    // Don't close here - let parent close on success
  };

  const handleClose = () => {
    setFormData({ name: "", width: "", length: "", height: "", image: undefined });
    setImagePreview(null);
    setImageError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreating ? "Add New Obstacle" : "Edit Obstacle"}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={obstacle?.name || "Obstacle name"}
            autoFocus
            required={isCreating}
          />
          <Divider />
          {(obstacle || isCreating) && (
            <>
              <DimensionInput
                label="Width"
                currentValue={obstacle?.width}
                value={formData.width}
                onChange={(value) => handleChange('width', value)}
                placeholder={isCreating ? "Width in feet" : "New width"}
                required={isCreating}
              />
              <DimensionInput
                label="Length"
                currentValue={obstacle?.length}
                value={formData.length}
                onChange={(value) => handleChange('length', value)}
                placeholder={isCreating ? "Length in feet" : "New length"}
                required={isCreating}
              />
              <DimensionInput
                label="Height"
                currentValue={obstacle?.height}
                value={formData.height}
                onChange={(value) => handleChange('height', value)}
                placeholder={isCreating ? "Height in feet" : "New height"}
                required={isCreating}
              />
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Obstacle Image
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                  color={imageError ? "error" : "primary"}
                >
                  {formData.image ? 'Change Image' : 'Upload Image'}
                </Button>
                {imageError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {imageError}
                  </Typography>
                )}
                {formData.image && !imageError && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Selected: {formData.image.name}
                  </Typography>
                )}
                {imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Stack>
        {!isCreating && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            * Leave dimension fields empty to keep current values
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {isCreating ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};